import { FC, useCallback, useContext, useMemo, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client';
import { Button, H2, Modal, Paragraph, Preloader } from '@smartshell/ui-kit';

import { AuthContext } from 'auth';
import { ClubList } from 'auth/ClubList';
import { USER_CLUBS } from 'auth/schema';
import { IUserClub, IUserClubsResponse } from 'auth/types';
import { LocalizationContext } from 'localization';
import { COPY_SETTINGS } from 'schemas';
import notificationService from 'services/notificationService';

import s from './CopySettingsModal.module.scss';
import { ICopySettingModalProps } from './interfaces';

export const CopySettingsModal: FC<ICopySettingModalProps> = ({ close }) => {
  const { t } = useContext(LocalizationContext);
  const {
    data: { tokens },
  } = useContext(AuthContext);

  const encodedToken = atob(tokens?.access_token?.split('.')[1] ?? '');
  const clubId = encodedToken ? JSON.parse(encodedToken)?.company_id : null;

  const [selectedClubId, setSelectedClubId] = useState<number | null>(null);

  const { data: clubsData, loading: clubsLoading } =
    useQuery<IUserClubsResponse>(USER_CLUBS, {
      fetchPolicy: 'network-only',
    });

  const [copySettings, { loading: copySettingsLoading }] = useMutation(
    COPY_SETTINGS,
    {
      onCompleted: () => {
        notificationService.info({
          t,
          type: 'success',
          title: t('settings.copy.success'),
        });
        close();
      },
      onError: ({ message }) => {
        notificationService.info({
          t,
          type: 'error',
          title: t('settings.copy.error.title'),
          message,
        });
      },
    }
  );

  const clubs = useMemo<IUserClub[] | null>(() => {
    if (!clubsData?.userClubs) return null;

    const filteredClubs = clubsData.userClubs.filter(
      ({ permitted }) => permitted
    );

    return filteredClubs.length > 1 ? filteredClubs : null;
  }, [clubsData]);

  const copySettingHandler = useCallback(() => {
    if (!selectedClubId) return;

    return copySettings({ variables: { copySettingsId: selectedClubId } });
  }, [selectedClubId]);

  return (
    <Modal onClose={close} className={s.container}>
      <div className={s.header}>
        <H2 className={s.title}>{t('settings.copy.modal.title')}</H2>
        <Paragraph className={s.text}>
          {t('settings.copy.modal.description')}
        </Paragraph>
      </div>
      <div className={s.content}>
        <Preloader
          hasData={!!clubs}
          noDataPlaceholder="Нет клубов"
          loading={clubsLoading}
        >
          {clubs && (
            <ClubList
              clubs={clubs}
              currentClubId={clubId}
              selectedClubId={selectedClubId}
              setSelectedClubId={setSelectedClubId}
              isCopySetting
            />
          )}
        </Preloader>
      </div>
      <div className={s.buttons}>
        <Button
          fullWidth
          disabled={selectedClubId == null || copySettingsLoading}
          onClick={copySettingHandler}
        >
          {t('settings.copy.modal.submit')}
        </Button>
        <Button variant="secondary" fullWidth onClick={close}>
          {t('cancel')}
        </Button>
      </div>
    </Modal>
  );
};
